import React, { useState, useEffect, useRef } from "react";
import { UTILS, INS } from "../config/API";
import { useNavigate } from 'react-router-dom';
import RadioField from "../components/RadioField";
import CheckboxField from "../components/CheckboxField";
import SignatureCanvas from "react-signature-canvas";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fetchProfileData } from '../utils/profile';
import appConfig, { getPortalKey } from "../config";
import tlingitLogo from "../assets/icons/tlingitRedBlack.png";
import BUILD_DATE from "../config/version";
import { withSanitizedFileName } from "../utils/sanitizeFileName";

const CCTHRegistration = () => {
  const navigate = useNavigate();
  const [formFields, setFormFields] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isAPILoading, setIsAPILoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [zipCodes, setZipCodes] = useState([]);
  const [showZipDropdown, setShowZipDropdown] = useState(false);
  const [picklistCache, setPicklistCache] = useState({});
  const [files, setFiles] = useState([{ id: 1, file: null, type: "" }]);
  const [showSignature, setShowSignature] = useState(false);
  const [signatureText, setSignatureText] = useState("");
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [fileUploadText, setFileUploadText] = useState("");
  const [isSigned, setIsSigned] = useState(false);
  const [date, setDate] = useState("");
  const [error, setError] = useState(null);
  const sigPad = useRef(null);
  const [fileTypes, setFileTypes] = useState("");
  const [profileData, setProfileData] = useState(null);
  const [config, setConfig] = useState({});
  const [appName, setAppName] = useState("");

  // Static values
  const iType = "ci";
  const sType = "publicaccountrequest";

  // Initialize config
  useEffect(() => {
    const key = getPortalKey();
    setConfig(appConfig[key] || appConfig.portal);
  }, []);

  useEffect(() => {
    if (config.name) {
      setAppName(config.name);
    }
  }, [config]);

  useEffect(() => {
    const fetchFormFields = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(UTILS, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "Accept": "application/json"
          },
          body: new URLSearchParams({
            action: "desc",
            itype: iType,
            stype: sType,
          }),
        });

        const text = await response.text();
        const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");

        let data;
        try {
          data = JSON.parse(cleanedText);
        } catch (parseError) {
          console.error("Failed to parse form fields:", parseError);
          throw new Error("Invalid server response format");
        }


        if (response.ok) {
          const filteredFields = data.Fields
            .filter(
              (field) =>
                ![
                  "itemid",
                  "autonumber",
                  "readonlydate",
                  "readonlypicklist",
                  "readonly",
                  "hidden",
                  "readonlylookup",
                  "linkedfield",
                  "inlinequery",
                  "person",
                ].includes(field.fieldtype)
            )
            .sort((a, b) => a.sortorder - b.sortorder);

          setFormFields(filteredFields);

          const initialFormData = {};
          filteredFields.forEach((field) => {
            if (field.defaultvalue) {
              initialFormData[field.fieldname] = field.defaultvalue;
            }
          });

          setFormData(initialFormData);

          if (data.Signature) {
            setShowSignature(data.Signature);
            setSignatureText(data.SignatureText || "");
          }
          if (data.FileUpload) {
            setShowFileUpload(data.FileUpload);
            setFileUploadText(data.FileUploadText || "");
          }
          if (data.FileTypes) {
            setFileTypes(data.FileTypes);
          }

          const picklistFields = filteredFields.filter(
            (field) => field.fieldtype === "picklist"
          );
          await Promise.all(
            picklistFields.map((field) => fetchDropDownFields(field.itemvaluegroup))
          );
        } else {
          throw new Error(data.message || "Failed to fetch form fields");
        }
      } catch (error) {
        console.error("Error loading form:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFormFields();
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await fetchProfileData();
        if (sType !== "addresshist") {
          setProfileData(data);
        } else {
          const transformedData = Object.entries(data).reduce((acc, [key, value]) => {
            const keyMap = {
              "Residential Zip": "Zip",
              "Residential Address": "Address",
              "Residential City": "City",
              "Residential State": "State"
            };
            acc[keyMap[key] || key] = value;
            return acc;
          }, {});

          setProfileData(transformedData);
        }
      } catch (error) {
        console.error("Error loading profile data:", error);
      }
    };
    loadProfile();
  }, []);

  useEffect(() => {
    if (!formFields.length || !profileData) return;

    setFormData(prev => {
      const updatedData = { ...prev };
      formFields.forEach(field => {
        const profileKey = Object.keys(profileData).find(
          key => key === field.displayname
        );
        if (profileKey && profileData[profileKey]) {
          updatedData[field.fieldname] = profileData[profileKey];
        }
      });
      return updatedData;
    });
  }, [formFields, profileData]);

  const fetchDropDownFields = async (itemValueGroup) => {
    if (picklistCache[itemValueGroup]?.data) return;

    setPicklistCache((prev) => ({
      ...prev,
      [itemValueGroup]: { ...prev[itemValueGroup], isLoading: true },
    }));

    try {
      const response = await fetch(UTILS, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json"
        },
        body: new URLSearchParams({ action: "idv", s: itemValueGroup || "YesNo" }),
      });

      const text = await response.text();
      const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");

      let data;
      try {
        data = JSON.parse(cleanedText);
      } catch (parseError) {
        console.error("Failed to parse dropdown data:", parseError);
        console.error("Original text:", text);
        throw new Error("Invalid dropdown data format");
      }

      setPicklistCache((prev) => ({
        ...prev,
        [itemValueGroup]: { data, isLoading: false },
      }));
    } catch (error) {
      console.error("Error fetching dropdown:", error);
      setPicklistCache((prev) => ({
        ...prev,
        [itemValueGroup]: { isLoading: false, error: error.message },
      }));
    }
  };

  const handleCheckboxGroupChange = (e, fieldName) => {
    const { value, checked } = e.target;

    setFormData(prev => {
      const currentValues = prev[fieldName] ? prev[fieldName].split(',') : [];
      const updatedValues = checked
        ? [...currentValues, value]
        : currentValues.filter(item => item !== value);

      return {
        ...prev,
        [fieldName]: updatedValues.join(',')
      };
    });
  };

  const formatSSNOnChange = (e) => {
    const input = e.target.value.replace(/\D/g, "");
    let formattedValue = "";

    if (input.length > 0) {
      formattedValue += input.substring(0, 3);
    }
    if (input.length > 3) {
      formattedValue += "-" + input.substring(3, 5);
    }
    if (input.length > 5) {
      formattedValue += "-" + input.substring(5, 9);
    }

    setFormData(prev => ({ ...prev, [e.target.name]: formattedValue }));
  };

  const formatPhoneOnChange = (e) => {
    const input = e.target;
    let phoneNumber = input.value.replace(/\D/g, '');
    if (phoneNumber.length > 10) {
      phoneNumber = phoneNumber.slice(0, 10);
    }

    let formattedPhone = phoneNumber;
    if (phoneNumber.length > 6) {
      formattedPhone = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
    } else if (phoneNumber.length > 3) {
      formattedPhone = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumber.length > 0) {
      formattedPhone = `(${phoneNumber}`;
    }

    input.value = formattedPhone;
    setFormData((prevFormData) => ({ ...prevFormData, [input.name]: formattedPhone }));
  };

  const fetchZipData = async (zip) => {
    try {
      const response = await fetch(UTILS, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json"
        },
        body: new URLSearchParams({ action: "zip", s: zip }),
      });

      const text = await response.text();
      const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");

      let data;
      try {
        data = JSON.parse(cleanedText);
      } catch (parseError) {
        console.error("Failed to parse ZIP data:", parseError);
        throw new Error("Invalid ZIP data format");
      }

      if (response.ok) {
        setZipCodes(data);
      } else {
        throw new Error(data.message || "Failed to fetch ZIP codes");
      }
    } catch (error) {
      console.error("ZIP lookup failed:", error);
      setZipCodes([]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'frc') {
      setInputValue(value);

      if (value.length >= 3) {
        fetchZipData(value);
        setShowZipDropdown(true);
      } else {
        setShowZipDropdown(false);
        setFormData((prevFormData) => ({ ...prevFormData, frc: "" }));
      }
    } else if (type === 'checkbox') {
      setFormData((prevFormData) => {
        const currentValues = prevFormData[name] || [];
        let updatedValues;

        if (checked) {
          updatedValues = [...currentValues, value];
        } else {
          updatedValues = currentValues.filter((item) => item !== value);
        }

        return {
          ...prevFormData,
          [name]: updatedValues,
        };
      });
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }
  };

  const handleZipSelection = (zip) => {
    setInputValue(zip.zipcode);
    setFormData((prevFormData) => ({ ...prevFormData, frc: zip.itemid }));
    setShowZipDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsAPILoading(true);
    setError(null);

    const formDataToSend = new FormData();
    formDataToSend.append('action', 'ins');
    formDataToSend.append('itype', iType);
    formDataToSend.append('stype', sType);

    if (showSignature && sigPad.current && !sigPad.current.isEmpty()) {
      try {
        const signatureDataUrl = sigPad.current.getTrimmedCanvas().toDataURL('image/png');
        const blob = await fetch(signatureDataUrl).then(res => res.blob());

        const signatureFile = new File([blob], 'signature.png', { type: 'image/png' });

        formDataToSend.append('file1', signatureFile);
        formDataToSend.append('artifacttype1', 'Signature');
        formDataToSend.append('signatureDate', date);
      } catch (error) {
        console.error("Error processing signature:", error);
      }
    }

    if (showFileUpload) {
      files.forEach((fileObj, index) => {
        if (fileObj.file) {
          const fileNumber = index + (showSignature && isSigned ? 2 : 1);
          formDataToSend.append(`file${fileNumber}`, withSanitizedFileName(fileObj.file));
          formDataToSend.append(`artifacttype`, fileObj.type.replace(/\s+/g, '') || "");
        }
      });
    }

    for (const key in formData) {
      if (formData.hasOwnProperty(key)) {
        formDataToSend.append(key, formData[key]);
      }
    }

    const fetchUrl = INS;

    try {
      const response = await fetch(fetchUrl, { method: "POST", body: formDataToSend });
      const text = await response.text();
      const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");

      let data;
      try {
        data = JSON.parse(cleanedText);
      } catch (parseError) {
        console.error("Failed to parse response:", parseError);
        console.error("Original text:", text);
        throw new Error("Invalid server response format");
      }

      if (data.result === "true") {
        alert("Thank you, your information has been received.");
        navigate("/login");
      } else {
        throw new Error(data.message || "Submission failed");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setError(error.message);
    } finally {
      setIsAPILoading(false);
    }
  };

  const handleAddMore = () => {
    setFiles([...files, { id: files.length + 1, file: null, type: "" }]);
  };

  const handleFileChange = (index, event) => {
    const updatedFiles = [...files];
    updatedFiles[index].file = event.target.files[0];
    setFiles(updatedFiles);
  };

  const handleFileTypeChange = (index, value) => {
    const updatedFiles = [...files];
    updatedFiles[index].type = value;
    setFiles(updatedFiles);
  };

  const handleClear = () => {
    if (sigPad.current) {
      sigPad.current.clear();
      setIsSigned(false);
    }
  };

  const handleEnd = () => {
    if (sigPad.current) {
      setIsSigned(!sigPad.current.isEmpty());
    }
  };


  if (!config.name) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-primary to-background">
        <div className="bg-white p-8 rounded-lg shadow-lg">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    const backgroundClass = appName === "My Tlingit & Haida Portal" 
      ? "bg-gradient-to-br from-[#303030] to-[#000000]" 
      : "bg-gradient-to-br from-primary to-background";
    const logoSrc = appName === "My Tlingit & Haida Portal" ? tlingitLogo : config.logo;

    return (
      <div className={`flex flex-col justify-center items-center min-h-screen px-4 py-8 ${backgroundClass}`}>
        <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md md:max-w-lg flex flex-col justify-center">
          <div className="flex flex-col items-center mb-8">
            <img src={logoSrc} alt="Logo" className="h-16 mb-4 transition-transform duration-300 hover:scale-105" />
          </div>
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full absolute border-4 border-gray-200"></div>
              <div className="w-16 h-16 rounded-full animate-spin border-4 border-primary border-t-transparent border-r-transparent"></div>

              <div className="absolute inset-0 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-primary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 14l-7 7m0 0l-7-7m7 7V3"
                  ></path>
                </svg>
              </div>
            </div>

            <div className="flex flex-col items-center space-y-2">
              <p className="text-gray-600 font-medium text-lg animate-pulse">Preparing Your Form...</p>
              <div className="flex space-x-2">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="w-2 h-2 bg-primary rounded-full animate-bounce"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  ></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const backgroundClass = appName === "My Tlingit & Haida Portal" 
      ? "bg-gradient-to-br from-[#303030] to-[#000000]" 
      : "bg-gradient-to-br from-primary to-background";
    const logoSrc = appName === "My Tlingit & Haida Portal" ? tlingitLogo : config.logo;

    return (
      <div className={`flex flex-col justify-center items-center min-h-screen px-4 py-8 ${backgroundClass}`}>
        <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md md:max-w-lg text-center">
          <div className="flex flex-col items-center mb-8">
            <img src={logoSrc} alt="Logo" className="h-16 mb-4 transition-transform duration-300 hover:scale-105" />
          </div>
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Form</h2>
          <p className="text-gray-700 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#711515] text-white px-6 py-2 rounded-lg hover:bg-[#5a1010] transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const backgroundClass = appName === "My Tlingit & Haida Portal" 
    ? "bg-gradient-to-br from-[#303030] to-[#000000]" 
    : "bg-gradient-to-br from-primary to-background";

  const logoSrc = appName === "My Tlingit & Haida Portal" ? tlingitLogo : config.logo;

  return (
    <div className={`flex flex-col justify-center items-center min-h-screen px-4 py-8 ${backgroundClass}`}>
      <div className="bg-white p-8 md:p-10 rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col justify-center border border-gray-100">
        <div className="flex flex-col items-center mb-10 pb-8 border-b border-gray-200">
          <div className="mb-4 p-3 bg-gray-50 rounded-full">
            <img src={logoSrc} alt="Logo" className="h-16 w-auto transition-transform duration-300 hover:scale-105" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">
            {config.name}
          </h1>
        
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-r-lg shadow-sm">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="font-medium">{error}</p>
            </div>
          </div>
        )}

        <div className="mb-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">Request Account</h2>
          <div className="w-20 h-1 bg-[#711515] mx-auto rounded-full"></div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col md:grid md:grid-cols-2 gap-6">
          {formFields.map((field) => (
            <div className={`${field.fieldtype === "label" ? "col-span-2" : "col-span-1"} ${field.fieldtype !== "label" ? "mb-1" : ""}`} key={field.displayid}>
              <label className={`block ${field.fieldtype === "label" ? "font-bold text-xl text-gray-900 w-full mb-4 pt-2" : "text-gray-700 font-semibold text-sm mb-2"} transition-colors`}>
                {field.required === "Y" && <span className="text-[#711515] mr-1 font-bold">*</span>}
                {field.displayname}
                {field.tooltip && (
                  <div className="inline-block relative ml-2 group">
                    <svg
                      className="w-5 h-5 text-gray-400 cursor-help hover:text-[#711515] transition-colors duration-200"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 shadow-xl">
                      {field.tooltip}
                      <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-3 h-3 bg-gray-900 rotate-45"></div>
                    </div>
                  </div>
                )}
              </label>
              {field.fieldtype === "lookup" ? (
                <div className="relative">
                  <input
                    type="text"
                    name={field.fieldname}
                    value={inputValue}
                    onChange={handleChange}
                    className="w-full p-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-[#711515] bg-white shadow-sm transition-all duration-200 hover:border-gray-400"
                    style={{ width: '100%' }}
                    required={field.required === "Y"}
                    maxLength={field.maxlength || ""}
                    autoComplete="off"
                    ref={inputRef}
                  />

                  {showZipDropdown && field.fieldname === 'frc' && (
                    <div
                      className="absolute z-10 mt-2 bg-white border border-gray-300 rounded-lg shadow-xl max-h-48 overflow-y-auto"
                      style={{ width: inputRef.current ? inputRef.current.offsetWidth : '100%' }}
                    >
                      {zipCodes.length > 0 ? (
                        zipCodes.map((zip) => (
                          <div
                            key={zip.itemid}
                            className="p-3 hover:bg-[#711515] hover:text-white cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                            onClick={() => handleZipSelection(zip)}
                          >
                            {zip.zipcode}
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-gray-500 text-center">No results found</div>
                      )}
                    </div>
                  )}
                </div>
              ) : field.fieldtype === "text" || field.fieldtype === "numeric" || field.fieldtype === "currency" || field.fieldtype === "textdate" ? (
                <>
                  <input
                    type="text"
                    name={field.fieldname}
                    value={formData[field.fieldname] || ""}
                    onChange={
                      field.onblur === "formatPhone(this);"
                        ? formatPhoneOnChange
                        : field.onfocus === "formatSSN(this);"
                          ? formatSSNOnChange
                          : handleChange
                    }
                    className="w-full p-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-[#711515] bg-white shadow-sm transition-all duration-200 hover:border-gray-400"
                    style={{ width: '100%' }}
                    required={field.required === "Y"}
                    maxLength={field.maxlength || ""}
                  />
                  {field.displayname === "Email Address" && emailError && (
                    <p className="text-[#711515] text-sm mt-2 font-medium flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {emailError}
                    </p>
                  )}
                </>
              ) : field.fieldtype === "textarea" ? (
                <textarea
                  name={field.fieldname}
                  value={formData[field.fieldname] || ""}
                  onChange={handleChange}
                  className="w-full p-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-[#711515] bg-white shadow-sm transition-all duration-200 hover:border-gray-400 resize-y"
                  style={{
                    width: field.fieldlength && parseInt(field.fieldlength, 10) > 100 
                      ? '100%' 
                      : field.fieldlength 
                        ? `${field.fieldlength}%` 
                        : '100%'
                  }}
                  required={field.required === "Y"}
                  rows="4"
                ></textarea>
              ) : field.fieldtype === "picklist" ? (
                <div className="flex flex-col w-full">
                  <div className="relative w-full">
                    <select
                      name={field.fieldname}
                      value={formData[field.fieldname] || ""}
                      onChange={handleChange}
                      className="w-full p-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-[#711515] bg-white shadow-sm transition-all duration-200 hover:border-gray-400 appearance-none cursor-pointer"
                      required={field.required === "Y"}
                    >
                      <option value="">Select {field.displayname}</option>
                      {picklistCache[field.itemvaluegroup]?.isLoading ? (
                        <option disabled>Loading options...</option>
                      ) : (
                        (picklistCache[field.itemvaluegroup]?.data || []).map((option) => (
                          <option key={option.valueid} value={option.valuetext}>
                            {option.valuetext}
                          </option>
                        ))
                      )}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                      <svg
                        className="h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                  {picklistCache[field.itemvaluegroup]?.error && (
                    <p className="text-red-500 text-sm mt-1">
                      {picklistCache[field.itemvaluegroup].error}
                    </p>
                  )}
                </div>
              ) : field.fieldtype === "datetimepl" ? (
                <div className="w-full">
                  <DatePicker
                    selected={formData[field.fieldname] ? new Date(formData[field.fieldname]) : null}
                    onChange={(date) =>
                      setFormData({
                        ...formData,
                        [field.fieldname]: date.toISOString()
                      })
                    }
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="MMMM d, yyyy h:mm aa"
                    className="w-full p-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-[#711515] bg-white shadow-sm transition-all duration-200 hover:border-gray-400"
                    required={field.required === "Y"}
                    placeholderText="Select date and time"
                  />
                </div>
              ) : field.fieldtype === "date" ? (
                <input
                  type="date"
                  name={field.fieldname}
                  value={formData[field.fieldname] || ""}
                  onChange={handleChange}
                  className="w-full p-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-[#711515] bg-white shadow-sm transition-all duration-200 hover:border-gray-400"
                  required={field.required === "Y"}
                />
              ) : field.fieldtype === "checkbox" ? (
                <CheckboxField
                  field={field}
                  formData={formData}
                  handleCheckboxGroupChange={handleCheckboxGroupChange}
                  picklistCache={picklistCache}
                  setPicklistCache={setPicklistCache}
                />
              ) : field.fieldtype === "radio" ? (
                <RadioField
                  field={field}
                  formData={formData}
                  handleChange={handleChange}
                  picklistCache={picklistCache}
                  setPicklistCache={setPicklistCache}
                />
              ) : null}
            </div>
          ))}

          {showSignature && (
            <div className="col-span-2 bg-gray-50 p-6 rounded-xl border border-gray-200">
              {signatureText && (
                <p className="text-gray-700 font-semibold mb-6 text-base leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: signatureText }} />
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-3 text-sm">Signature:</label>
                  <div className="border-2 border-gray-300 rounded-lg bg-white p-4 relative shadow-sm hover:border-[#711515] transition-colors duration-200">
                    <SignatureCanvas
                      ref={sigPad}
                      penColor="#0000FF"
                      canvasProps={{ width: 450, height: 120, className: "sigCanvas" }}
                      onEnd={handleEnd}
                    />
                    <button
                      type="button"
                      onClick={handleClear}
                      className="absolute bottom-3 right-3 bg-red-500 text-white px-4 py-2 text-sm rounded-lg hover:bg-red-600 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
                    >
                      Clear Signature
                    </button>
                  </div>
                  {!isSigned && (
                    <p className="text-red-500 text-sm mt-2 font-medium">Signature is required</p>
                  )}
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-3 text-sm">Date:</label>
                  <input
                    type="date"
                    name="signatureDate"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full p-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-[#711515] bg-white shadow-sm transition-all duration-200 hover:border-gray-400"
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {showFileUpload && (
            <div className="col-span-2 bg-gray-50 p-6 rounded-xl border border-gray-200">
              {fileUploadText && (
                <p className="text-gray-700 font-semibold mb-6 text-base leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: fileUploadText }} />
              )}
              <div className="space-y-5">
                {files.map((file, index) => (
                  <div key={file.id} className="flex flex-col space-y-3 bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex flex-col md:flex-row md:items-end gap-4">
                      <div className="w-full md:w-1/3">
                        <label className="text-gray-700 font-semibold mb-2 block text-sm">
                          Document Type {index + 1}:
                        </label>
                        <select
                          value={file.type || ""}
                          onChange={(e) => handleFileTypeChange(index, e.target.value)}
                          className="w-full p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-[#711515] bg-white shadow-sm transition-all duration-200 hover:border-gray-400 appearance-none cursor-pointer"
                          required
                        >
                          <option value="">Select Document Type</option>
                          {fileTypes && fileTypes.split(',').map((type, i) => (
                            <option key={i} value={type.trim()}>
                              {type.trim()}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="w-full md:w-2/3">
                        <label className="text-gray-700 font-semibold mb-2 block text-sm">
                          Upload File {index + 1}:
                        </label>
                        <input
                          type="file"
                          onChange={(e) => handleFileChange(index, e)}
                          className="w-full border-2 border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-[#711515] focus:border-[#711515] bg-white shadow-sm transition-all duration-200 hover:border-gray-400 cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#711515] file:text-white hover:file:bg-[#5a1010] file:cursor-pointer"
                          required
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={handleAddMore}
                className="mt-4 text-[#711515] hover:text-[#5a1010] font-semibold text-sm transition-colors duration-200 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add More Documents
              </button>
            </div>
          )}

          <div className="col-span-2 flex justify-end items-center mt-8 pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={isAPILoading || (showSignature && !isSigned)}
              className={`bg-[#711515] text-white px-8 py-3 rounded-lg ${isAPILoading || (showSignature && !isSigned) ? "opacity-50 cursor-not-allowed" : "hover:bg-[#5a1010] shadow-md hover:shadow-lg"} transition-all duration-200 font-semibold flex items-center gap-2`}
            >
              {isAPILoading ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  Submit
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </form>

        <div className="flex justify-center pt-6 mt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="text-sm text-primary hover:text-secondary font-medium transition-colors duration-200 flex items-center gap-2 group"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Login
          </button>
        </div>
      </div>
      <footer className="text-center text-sm text-white mt-8">
        <p>TribeVue® Portal v{BUILD_DATE}</p>
        <p>© 2026 Tribal D, Inc.</p>
      </footer>
    </div>
  );
};

export default CCTHRegistration;
